<template>
<page-wrapper>
  <v-row>
    <v-col cols="12" md="4">
      <v-card flat outlined>
        <v-card-title>Categories</v-card-title>
        <v-list dense>
          <template v-for="(items, sector) in categoriesBySector">
            <v-subheader :key="'hdr-' + sector">{{ sector }}</v-subheader>
            <v-list-item v-for="item in items" :key="item.category">
              <v-list-item-content>{{ item.category }}</v-list-item-content>
              <v-list-item-action class="flex-row align-center">
                <v-menu offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon :color="item.colour" v-bind="attrs" v-on="on" class="mr-2">mdi-circle</v-icon>
                  </template>
                  <v-color-picker v-model="item.colour" mode="hexa"
                                  @update:color="updateCategoryColour(item, $event)"></v-color-picker>
                </v-menu>
                <v-icon small @click="deleteCategory(item.category)">mdi-delete</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
        <v-card-actions class="px-4 pb-4">
          <v-combobox v-model="newCategorySector" :items="availableSectors" label="Sector"
                      dense hide-details class="sector-field"></v-combobox>
          <v-text-field v-model="newCategory" label="New category" dense hide-details
                        @keyup.enter="addCategory"></v-text-field>
          <v-menu offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-icon :color="newCategoryColour" v-bind="attrs" v-on="on" class="mx-2">mdi-circle</v-icon>
            </template>
            <v-color-picker v-model="newCategoryColour" mode="hexa" hide-inputs></v-color-picker>
          </v-menu>
          <v-btn text color="primary" @click="addCategory">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-col>

    <v-col cols="12" md="4">
      <v-card flat outlined>
        <v-card-title>Effects</v-card-title>
        <v-list dense>
          <template v-for="(items, discipline) in effectsByDiscipline">
            <v-subheader :key="'hdr-' + discipline">{{ discipline }}</v-subheader>
            <v-list-item v-for="item in items" :key="item.effect">
              <v-list-item-content>{{ item.effect }}</v-list-item-content>
              <v-list-item-action class="flex-row align-center">
                <v-menu offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon :color="item.colour" v-bind="attrs" v-on="on" class="mr-2">mdi-circle</v-icon>
                  </template>
                  <v-color-picker v-model="item.colour" mode="hexa"
                                  @update:color="updateEffectColour(item, $event)"></v-color-picker>
                </v-menu>
                <v-icon small @click="deleteEffect(item.effect)">mdi-delete</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
        <v-card-actions class="px-4 pb-4">
          <v-combobox v-model="newEffectDiscipline" :items="availableDisciplines" label="Discipline"
                      dense hide-details class="sector-field"></v-combobox>
          <v-text-field v-model="newEffect" label="New effect" dense hide-details
                        @keyup.enter="addEffect"></v-text-field>
          <v-menu offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-icon :color="newEffectColour" v-bind="attrs" v-on="on" class="mx-2">mdi-circle</v-icon>
            </template>
            <v-color-picker v-model="newEffectColour" mode="hexa" hide-inputs></v-color-picker>
          </v-menu>
          <v-btn text color="primary" @click="addEffect">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-col>

    <v-col cols="12" md="4">
      <v-card flat outlined>
        <v-card-title>Tags</v-card-title>
        <v-virtual-scroll :items="websiteMeta.tags" item-height="32" height="400">
          <template v-slot:default="{ item }">
            <v-list-item dense :key="item">
              <v-list-item-content>{{ item }}</v-list-item-content>
              <v-list-item-action>
                <v-icon small @click="deleteTag(item)">mdi-delete</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-virtual-scroll>
        <v-card-actions class="px-4 pb-4">
          <v-text-field v-model="newTag" label="New tag" dense hide-details
                        @keyup.enter="addTag"></v-text-field>
          <v-btn text color="primary" @click="addTag">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</page-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import * as api from '../api'
import PageWrapper from '../components/ui/PageWrapper'

export default {
  name: 'WebsiteMeta',
  components: { PageWrapper },
  data() {
    return {
      defaultColour: '#555555',
      newCategorySector: '',
      newCategory: '',
      newCategoryColour: '',
      newEffectDiscipline: '',
      newEffect: '',
      newEffectColour: '',
      newTag: '',
      colourUpdateTimers: {}
    }
  },
  computed: {
    ...mapState(['websiteMeta']),
    categoriesBySector() {
      return this.websiteMeta.categories.reduce((acc, item) => {
        if (!acc[item.sector]) acc[item.sector] = []
        acc[item.sector].push(item)
        return acc
      }, {})
    },
    effectsByDiscipline() {
      return this.websiteMeta.effects.reduce((acc, item) => {
        if (!acc[item.discipline]) acc[item.discipline] = []
        acc[item.discipline].push(item)
        return acc
      }, {})
    },
    availableSectors() {
      return [...new Set(this.websiteMeta.categories.map(c => c.sector))]
    },
    availableDisciplines() {
      return [...new Set(this.websiteMeta.effects.map(e => e.discipline))]
    }
  },
  methods: {
    async refresh() {
      await this.$store.dispatch('getWebsiteMeta', true)
    },
    updateCategoryColour(item, colour) {
      clearTimeout(this.colourUpdateTimers[item.category])
      this.colourUpdateTimers[item.category] = setTimeout(() => {
        api.put('/website/category', { category: item.category, colour: colour.hex })
      }, 400)
    },
    updateEffectColour(item, colour) {
      clearTimeout(this.colourUpdateTimers[item.effect])
      this.colourUpdateTimers[item.effect] = setTimeout(() => {
        api.put('/website/effect', { effect: item.effect, colour: colour.hex })
      }, 400)
    },
    async addCategory() {
      if (!this.newCategory.trim()) return
      await api.post('/website/category', {
        sector: this.newCategorySector.trim(),
        category: this.newCategory.trim(),
        colour: this.newCategoryColour
      })
      this.newCategorySector = ''
      this.newCategory = ''
      this.newCategoryColour = this.defaultColour
      await this.refresh()
    },
    async deleteCategory(category) {
      await api.del('/website/category', { category })
      await this.refresh()
    },
    async addEffect() {
      if (!this.newEffect.trim()) return
      await api.post('/website/effect', {
        discipline: this.newEffectDiscipline.trim(),
        effect: this.newEffect.trim(),
        colour: this.newEffectColour
      })
      this.newEffectDiscipline = ''
      this.newEffect = ''
      this.newEffectColour = this.defaultColour
      await this.refresh()
    },
    async deleteEffect(effect) {
      await api.del('/website/effect', { effect })
      await this.refresh()
    },
    async addTag() {
      if (!this.newTag.trim()) return
      await api.post('/website/tag', { tag: this.newTag.trim() })
      this.newTag = ''
      await this.refresh()
    },
    async deleteTag(tag) {
      await api.del('/website/tag', { tag })
      await this.refresh()
    }
  },
  created() {
    this.$store.dispatch('getWebsiteMeta')
    this.newCategoryColour = this.defaultColour
    this.newEffectColour = this.defaultColour
  }
}
</script>

<style scoped lang="scss">
.sector-field {
  max-width: 130px;
}
</style>
